import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import AddBatchModal from './AddBatchModal';

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

interface FeesSlab {
  fees_slab_id: string;
  base_fee: number;
  additional_fee: number;
  total_fee: number;
  created_at: string | null;
  updated_at: string | null;
}

interface AssignedTeacher {
  assignment_id: string;
  teacher_id: string;
  name: string;
  email: string;
  mobile: string;
  employee_id: string;
  profile_picture: string | null;
  subject: string | null;
  assigned_at: string;
  status: string;
}

interface BatchDetails {
  batch_id: string;
  institute_id: string;
  branch_id: string;
  batch_name: string;
  batch_time: string;
  start_month: string;
  start_year: number;
  end_month: string;
  end_year: number;
  is_active: boolean;
  created_at: string;
  course: Course | null;
  fees_slab: FeesSlab | null;
  enrolled_students_count: number;
  assigned_teachers: AssignedTeacher[];
  assigned_teachers_count: number;
}

interface BatchForModal {
  batch_id: string;
  institute_id: string;
  branch_id: string;
  course_id: string;
  batch_name: string;
  batch_time: string;
  start_month: string;
  start_year: number;
  end_month: string;
  end_year: number;
  is_active: boolean;
  created_at: string | null;
}

interface CourseForModal {
  course_id: string;
  name: string;
  title: string;
  fees_slab?: {
    fees_slab_id: string;
    base_fee: number;
    minimum_fee: number;
    maximum_fee: number;
    status: string;
    created_at?: string;
  } | null;
}

// ── Design tokens (navy / gold — matches Batches.tsx) ──────────────────────
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  gold:        '#fbb21f',
  goldLight:   '#ffc93c',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#E5E7EB',
  bg:          '#F5F7FA',
  textMuted:   '#9ca3af',
  textSub:     '#6b7280',
  textBody:    '#1f2937',
};

const BatchDetailsPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { profile } = useOperatorProfileStore();

  const [batch, setBatch] = useState<BatchDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (batchId) fetchBatchDetails();
  }, [batchId]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/v1/batches/${batchId}/details`);
      if (res.data.success) {
        setBatch(res.data.data);
      } else {
        setError('Failed to load batch details');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const getBatchForModal = (): BatchForModal | null => {
    if (!batch) return null;
    return {
      batch_id:     batch.batch_id,
      institute_id: batch.institute_id,
      branch_id:    batch.branch_id,
      course_id:    batch.course?.course_id || '',
      batch_name:   batch.batch_name,
      batch_time:   batch.batch_time,
      start_month:  batch.start_month,
      start_year:   batch.start_year,
      end_month:    batch.end_month,
      end_year:     batch.end_year,
      is_active:    batch.is_active,
      created_at:   batch.created_at,
    };
  };

  const getCoursesForModal = (): CourseForModal[] => {
    if (!batch?.course) return [];
    return [{
      course_id: batch.course.course_id,
      name:      batch.course.name,
      title:     batch.course.title,
      fees_slab: batch.fees_slab ? {
        fees_slab_id: batch.fees_slab.fees_slab_id,
        base_fee:     batch.fees_slab.base_fee,
        minimum_fee:  0,
        maximum_fee:  batch.fees_slab.total_fee,
        status:       'active',
        created_at:   batch.fees_slab.created_at || undefined,
      } : null,
    }];
  };

  const handleEditSuccess = () => {
    toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Batch updated successfully!', life: 3000 });
    setShowEditModal(false);
    fetchBatchDetails();
  };

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  const formatCurrency = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading batch details...</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !batch) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="error" text={error || 'Batch not found'} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
        <button
          onClick={() => navigate('/batches')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Back to Batches
        </button>
      </div>
    );
  }

  // ── Main ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* ── Hero Header — navy gradient (mirrors Batches.tsx hero) ── */}
      <div style={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: C.white, position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14,34,93,0.3)'
      }}>
        {/* Decorative orbs */}
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Back button */}
          <button
            onClick={() => navigate('/batches')}
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: C.white, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center',
              gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <i className="pi pi-arrow-left" /> Back to Batches
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
            {/* Left — title + chips */}
            <div>
              {/* Badge pill — matches Batches.tsx breadcrumb badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '4px 12px',
                background: C.goldDim, border: `1px solid ${C.goldBorder}`,
                borderRadius: '20px', marginBottom: '12px',
                fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px',
                textTransform: 'uppercase', color: C.gold
              }}>
                <i className="pi pi-th-large" style={{ fontSize: '0.7rem' }} />
                Batch Details
              </div>

              <h1 style={{ margin: '0 0 6px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
                {batch.batch_name}
              </h1>
              <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                {batch.course?.title || batch.course?.name || 'No Course Assigned'}
              </p>

              {/* Info chips */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {[
                  { icon: 'pi-clock',         label: batch.batch_time },
                  { icon: 'pi-calendar',       label: `${batch.start_month} ${batch.start_year} – ${batch.end_month} ${batch.end_year}` },
                  { icon: 'pi-users',          label: `${batch.enrolled_students_count} Students` },
                  { icon: 'pi-user',           label: `${batch.assigned_teachers_count} Teacher${batch.assigned_teachers_count !== 1 ? 's' : ''}` },
                  ...(batch.fees_slab ? [{ icon: 'pi-indian-rupee', label: formatCurrency(batch.fees_slab.total_fee) }] : [])
                ].map(chip => (
                  <div key={chip.label} style={{
                    background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
                    padding: '8px 14px', borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}>
                    <i className={`pi ${chip.icon}`} style={{ fontSize: '0.8rem', color: C.gold }} />
                    <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — status badges + edit button */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
              {/* Status badges */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{
                  padding: '4px 12px', borderRadius: '20px',
                  fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px',
                  ...(batch.is_active
                    ? { background: C.gold, color: C.navy }
                    : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' })
                }}>
                  {batch.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                {batch.course && (
                  <span style={{
                    padding: '4px 12px', borderRadius: '20px',
                    fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px',
                    background: batch.course.status === 'active' ? C.goldDim : 'rgba(255,255,255,0.1)',
                    color: batch.course.status === 'active' ? C.gold : 'rgba(255,255,255,0.5)',
                    border: `1px solid ${batch.course.status === 'active' ? C.goldBorder : 'rgba(255,255,255,0.2)'}`
                  }}>
                    {batch.course.status.toUpperCase()}
                  </span>
                )}
              </div>

              {/* Edit Batch — gold CTA (mirrors ADD BATCH button) */}
              <button
                onClick={() => setShowEditModal(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', background: C.gold,
                  border: 'none', borderRadius: '12px', color: C.navy,
                  fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(251,178,31,0.4)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '0.3px'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)';
                  e.currentTarget.style.background = C.goldLight;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)';
                  e.currentTarget.style.background = C.gold;
                }}
              >
                <i className="pi pi-pencil" style={{ fontSize: '0.9rem' }} />
                EDIT BATCH
              </button>

              <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
                Created {formatDate(batch.created_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body Grid ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', marginBottom: '24px' }}>

        {/* ── Left column ────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Batch Information */}
          <SectionCard title="Batch Information" icon="pi-bookmark">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
              <InfoField label="Batch Name"  value={batch.batch_name} />
              <InfoField label="Batch Time"  value={batch.batch_time} />
              <InfoField label="Start"       value={`${batch.start_month} ${batch.start_year}`} />
              <InfoField label="End"         value={`${batch.end_month} ${batch.end_year}`} />
              <InfoField label="Created On"  value={formatDate(batch.created_at)} />
              <InfoField label="Status"      value={batch.is_active ? 'Active' : 'Inactive'} valueColor={batch.is_active ? '#15803d' : '#b91c1c'} />
            </div>
          </SectionCard>

          {/* Course Information */}
          {batch.course && (
            <SectionCard title="Course Information" icon="pi-book">
              <div style={{
                background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
                borderRadius: '12px', padding: '20px', color: C.white,
                display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', overflow: 'hidden'
              }}>
                {/* orb */}
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: C.goldDim, borderRadius: '50%', filter: 'blur(20px)', pointerEvents: 'none' }} />
                <div style={{
                  width: '56px', height: '56px', flexShrink: 0,
                  background: C.goldDim, borderRadius: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${C.goldBorder}`
                }}>
                  <i className="pi pi-book" style={{ fontSize: '1.4rem', color: C.gold }} />
                </div>
                <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: '700', color: C.white }}>{batch.course.title}</p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                    Code: <strong style={{ color: C.gold }}>{batch.course.name}</strong>
                  </p>
                  <span style={{
                    display: 'inline-block', padding: '4px 12px', borderRadius: '20px',
                    fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase',
                    background: batch.course.status === 'active' ? C.gold : 'rgba(255,255,255,0.15)',
                    color: batch.course.status === 'active' ? C.navy : 'rgba(255,255,255,0.6)'
                  }}>
                    {batch.course.status}
                  </span>
                </div>
              </div>
            </SectionCard>
          )}

          {/* Fees Information */}
          <SectionCard title="Fees Information" icon="pi-indian-rupee">
            {batch.fees_slab ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {[
                    { label: 'Base Fee',       value: batch.fees_slab.base_fee,       bg: 'rgba(14,34,93,0.06)', border: 'rgba(14,34,93,0.12)', color: C.navy },
                    { label: 'Additional Fee', value: batch.fees_slab.additional_fee, bg: 'rgba(251,178,31,0.08)', border: 'rgba(251,178,31,0.25)', color: '#92580a' },
                    { label: 'Total Fee',      value: batch.fees_slab.total_fee,      bg: C.navy, border: C.navy, color: C.gold, dark: true },
                  ].map(item => (
                    <div key={item.label} style={{
                      background: item.bg, border: `1px solid ${item.border}`,
                      borderRadius: '12px', padding: '16px', textAlign: 'center'
                    }}>
                      <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', color: item.dark ? 'rgba(251,178,31,0.7)' : C.textMuted }}>
                        {item.label}
                      </p>
                      <p style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800', color: item.color }}>
                        {formatCurrency(item.value)}
                      </p>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: '0.78rem', color: C.textMuted, fontWeight: '600' }}>
                    <i className="pi pi-history" style={{ marginRight: '6px', fontSize: '0.72rem' }} />
                    Last updated
                  </span>
                  <span style={{ fontSize: '0.82rem', color: C.navy, fontWeight: '600' }}>
                    {formatDate(batch.fees_slab.updated_at)}
                  </span>
                </div>
              </div>
            ) : (
              <EmptyState icon="pi-indian-rupee" message="No fees slab configured for this batch." />
            )}
          </SectionCard>

          {/* Assigned Teachers */}
          <SectionCard title={`Assigned Teachers (${batch.assigned_teachers_count})`} icon="pi-user">
            {batch.assigned_teachers.length === 0 ? (
              <EmptyState icon="pi-user" message="No teachers assigned to this batch yet." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {batch.assigned_teachers.map(teacher => (
                  <TeacherCard key={teacher.assignment_id} teacher={teacher} formatDate={formatDate} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── Right column ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* At a Glance */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            {/* Section header — matches SectionCard */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-chart-bar" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>At a Glance</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: 'pi-users',        label: 'Enrolled Students', value: batch.enrolled_students_count,                              isNum: true  },
                { icon: 'pi-user',         label: 'Assigned Teachers', value: batch.assigned_teachers_count,                              isNum: true  },
                { icon: 'pi-indian-rupee', label: 'Total Batch Fee',   value: batch.fees_slab ? formatCurrency(batch.fees_slab.total_fee) : '—', isNum: false },
              ].map(stat => (
                <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: '14px', background: C.bg, padding: '14px', borderRadius: '12px', border: `1px solid ${C.border}` }}>
                  <div style={{ width: '44px', height: '44px', flexShrink: 0, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`pi ${stat.icon}`} style={{ color: C.gold, fontSize: '1rem' }} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.7rem', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{stat.label}</p>
                    <p style={{ margin: 0, fontSize: stat.isNum ? '1.6rem' : '1.1rem', fontWeight: '800', color: C.navy, lineHeight: 1.1 }}>{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Schedule Widget */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-calendar" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Schedule</h3>
            </div>

            {/* Time pill */}
            <div style={{
              background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
              borderRadius: '12px', padding: '16px', marginBottom: '12px',
              display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '70px', height: '70px', background: C.goldDim, borderRadius: '50%', filter: 'blur(20px)', pointerEvents: 'none' }} />
              <i className="pi pi-clock" style={{ fontSize: '1.4rem', color: C.gold, position: 'relative', zIndex: 1 }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{ margin: '0 0 2px 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Daily Time</p>
                <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.white }}>{batch.batch_time}</p>
              </div>
            </div>

            {/* Duration range */}
            <div style={{ background: C.bg, borderRadius: '10px', padding: '14px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <p style={{ margin: '0 0 3px 0', fontSize: '0.68rem', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Start</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: C.navy }}>{batch.start_month} {batch.start_year}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: '0 0 3px 0', fontSize: '0.68rem', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>End</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: C.navy }}>{batch.end_month} {batch.end_year}</p>
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height: '6px', background: C.border, borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '40%', background: `linear-gradient(90deg, ${C.navy}, ${C.gold})`, borderRadius: '4px' }} />
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-bolt" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Quick Actions</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { icon: 'pi-pencil', label: 'Edit Batch',      onClick: () => setShowEditModal(true) },
                { icon: 'pi-users',  label: 'View Students',   onClick: () => navigate('/students') },
                { icon: 'pi-user',   label: 'Manage Teachers', onClick: () => navigate('/teachers') },
              ].map(action => (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  style={{
                    width: '100%', padding: '11px 14px',
                    background: C.bg, border: `1px solid ${C.border}`,
                    borderRadius: '10px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '10px',
                    transition: 'all 0.2s ease', textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = C.navy;
                    e.currentTarget.style.borderColor = C.navy;
                    (e.currentTarget.querySelector('.qa-icon') as HTMLElement).style.color = C.gold;
                    (e.currentTarget.querySelector('.qa-label') as HTMLElement).style.color = C.white;
                    (e.currentTarget.querySelector('.qa-chevron') as HTMLElement).style.color = 'rgba(255,255,255,0.4)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = C.bg;
                    e.currentTarget.style.borderColor = C.border;
                    (e.currentTarget.querySelector('.qa-icon') as HTMLElement).style.color = C.navy;
                    (e.currentTarget.querySelector('.qa-label') as HTMLElement).style.color = C.textBody;
                    (e.currentTarget.querySelector('.qa-chevron') as HTMLElement).style.color = C.textMuted;
                  }}
                >
                  <i className={`pi ${action.icon} qa-icon`} style={{ fontSize: '0.9rem', color: C.navy, transition: 'color 0.2s' }} />
                  <span className="qa-label" style={{ fontSize: '0.85rem', fontWeight: '600', color: C.textBody, transition: 'color 0.2s' }}>{action.label}</span>
                  <i className="pi pi-chevron-right qa-chevron" style={{ marginLeft: 'auto', fontSize: '0.7rem', color: C.textMuted, transition: 'color 0.2s' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Modal ────────────────────────────────────────────────────── */}
      <AddBatchModal
        visible={showEditModal}
        instituteId={profile?.institute_id || batch.institute_id}
        branchId={profile?.branch_id || batch.branch_id}
        courses={getCoursesForModal()}
        editingBatch={getBatchForModal()}
        onHide={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div style={{
    background: '#ffffff', borderRadius: '16px', padding: '24px',
    boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
      {/* Icon box — navy gradient with gold icon */}
      <div style={{
        width: '40px', height: '40px',
        background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
        borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '1.1rem' }} />
      </div>
      <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#0e225d' }}>{title}</h2>
    </div>
    {children}
  </div>
);

const InfoField: React.FC<{ label: string; value: string | null; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div>
    {/* Uppercase label — matches card body labels in Batches.tsx */}
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      {label}
    </p>
    <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: valueColor || '#1f2937' }}>
      {value || '-'}
    </p>
  </div>
);

const EmptyState: React.FC<{ icon: string; message: string }> = ({ icon, message }) => (
  <div style={{ padding: '40px 20px', textAlign: 'center', background: '#F5F7FA', borderRadius: '12px', border: '2px dashed #E5E7EB' }}>
    <div style={{ width: '52px', height: '52px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
      <i className={`pi ${icon}`} style={{ fontSize: '1.5rem', color: '#0e225d', opacity: 0.25 }} />
    </div>
    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>{message}</p>
  </div>
);

const TeacherCard: React.FC<{ teacher: AssignedTeacher; formatDate: (d: string) => string }> = ({ teacher, formatDate }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '16px', background: '#F5F7FA',
      borderRadius: '12px', border: '1px solid #E5E7EB',
      transition: 'all 0.2s ease'
    }}
    onMouseEnter={e => {
      (e.currentTarget as HTMLDivElement).style.background = 'rgba(14,34,93,0.04)';
      (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(251,178,31,0.4)';
    }}
    onMouseLeave={e => {
      (e.currentTarget as HTMLDivElement).style.background = '#F5F7FA';
      (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E7EB';
    }}
  >
    <Avatar
      image={teacher.profile_picture || undefined}
      icon={!teacher.profile_picture ? 'pi pi-user' : undefined}
      size="large"
      shape="circle"
      style={{
        width: '52px', height: '52px', flexShrink: 0,
        backgroundColor: teacher.profile_picture ? 'transparent' : '#0e225d',
        color: '#fbb21f',
        border: '2px solid rgba(14,34,93,0.15)'
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: '0 0 3px 0', fontSize: '0.95rem', fontWeight: '700', color: '#0e225d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {teacher.name}
      </p>
      <p style={{ margin: '0 0 2px 0', fontSize: '0.78rem', color: '#6b7280' }}>
        <i className="pi pi-id-card" style={{ marginRight: '5px', fontSize: '0.7rem' }} />{teacher.employee_id || '—'}
        {teacher.subject && (
          <span style={{ marginLeft: '10px', color: '#9ca3af' }}>
            <i className="pi pi-book" style={{ marginRight: '4px', fontSize: '0.7rem' }} />{teacher.subject}
          </span>
        )}
      </p>
      <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>Assigned {formatDate(teacher.assigned_at)}</p>
    </div>
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      {/* Active badge mirrors Batches.tsx status badge */}
      <span style={{
        display: 'inline-block', padding: '4px 10px', borderRadius: '20px',
        fontSize: '0.68rem', fontWeight: '700', textTransform: 'uppercase',
        ...(teacher.status === 'active'
          ? { background: '#fbb21f', color: '#0e225d' }
          : { background: 'rgba(14,34,93,0.08)', color: '#9ca3af', border: '1px solid #E5E7EB' })
      }}>
        {teacher.status}
      </span>
      <p style={{ margin: '6px 0 0 0', fontSize: '0.72rem', color: '#9ca3af' }}>{teacher.mobile}</p>
    </div>
  </div>
);

export default BatchDetailsPage;
